<!--
  =====================================================================
  FILIALI PAGE - BRANCHES DIRECTORY
  =====================================================================
  
  Displays company branches organized in a two-column responsive grid.
  
  FEATURES:
  - Loads all branches data from store
  - Groups branches into left and right columns for optimal layout
  - Shows page title and introductory text
  - Responsive grid that collapses to single column on mobile
  - Shows loading and error states
  
  LAYOUT:
  - Page header with title and intro text
  - Two-column grid of branch groups on desktop
  - Single column on mobile devices
  
  =====================================================================
-->

<template>
  <div>
    <SiteHeader />
    <main class="filiali-page">
      <div v-if="state.loading">Caricamento...</div>
      
      <div v-else-if="state.error">Errore durante il caricamento delle filiali</div>
      
      <div v-else>
        <div class="page-header">
          <h1 class="page-title">{{ state.page.title }}</h1>
          <p v-if="state.page.text" class="page-intro">{{ state.page.text }}</p>
        </div>

        <div class="groups-grid">
          <div class="col col-left">
            <component
              v-for="(section, index) in leftGroups"
              :key="'left-' + index"
              :is="SectionFilialiGroup"
              :content="section.content"
            />
          </div>
          <div class="col col-right">
            <component
              v-for="(section, index) in rightGroups"
              :key="'right-' + index"
              :is="SectionFilialiGroup"
              :content="section.content"
            />
          </div>
        </div>
      </div>
    </main>
    <ArrowUp />
    <SiteFooter />
  </div>
</template>

<script setup>
import { onMounted, computed } from 'vue'
import { state, loadPageContent, getFilialiGroups } from '@/store/index.js'

// ───────────────────────────────────────────────────────────────────
// SHARED LAYOUT COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SiteHeader from '@/components/sections/shared/SiteHeader.vue'
import SiteFooter from '@/components/sections/shared/SiteFooter.vue'

// ───────────────────────────────────────────────────────────────────
// FILIALI-SPECIFIC COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SectionFilialiGroup from '@/components/sections/filiali/SectionFilialiGroup.vue'

// ───────────────────────────────────────────────────────────────────
// UTILITIES
// ───────────────────────────────────────────────────────────────────
import ArrowUp from '@/components/ArrowUp.vue'

// ───────────────────────────────────────────────────────────────────
// COMPUTED PROPERTIES
// ───────────────────────────────────────────────────────────────────

/**
 * Gets all branch groups from store
 * @returns {Array} Array of branch group sections
 */
const filialiGroups = computed(() => getFilialiGroups())

/**
 * Groups first half of branches for left column
 * @returns {Array} First 2 branch groups
 */
const leftGroups = computed(() => filialiGroups.value.slice(0, 2))

/**
 * Groups second half of branches for right column
 * @returns {Array} Remaining branch groups (2+)
 */
const rightGroups = computed(() => filialiGroups.value.slice(2))

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ───────────────────────────────────────────────────────────────────

/**
 * Load branches data on component mount
 */
onMounted(async () => {
  await loadPageContent('/filiali')
})
</script>

<style scoped lang="scss">
@import '@/assets/styles/partials/variables';
@import '@/assets/styles/partials/compat';

/* ------------------------------------------------------------------
   Page container
------------------------------------------------------------------ */
.filiali-page {
  padding: $space-2 $container-padding-x;
  min-height: calc(100vh - var(--header-total-height) - var(--footer-total-height));
  display: flex;
  flex-direction: column;
  @include flex-gap($space-4, column);
}

/* ------------------------------------------------------------------
   Page header with title and intro text
------------------------------------------------------------------ */
.page-header {
  max-width: $container-max-width;
  margin: 0 auto $space-2 auto;
  padding: 0 $container-padding-x;
  text-align: center;
}

.page-title {
  font-size: $font-title;
  font-weight: 300;
  color: $clr-brand-dark;
  margin: 0 0 $space-1 0;
}

.page-intro {
  font-size: $font-base;
  color: $clr-brand-gray;
  line-height: $line-height-base;
  margin: 0;
}

/* ------------------------------------------------------------------
   Two-column grid for branch groups
------------------------------------------------------------------ */
.groups-grid {
  max-width: $container-max-width;
  margin: 0 auto;
  padding: 0 $container-padding-x;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: $space-2;
  align-items: start;
}

.col { 
  display: grid; 
  gap: 5.6rem; 
}

/* ------------------------------------------------------------------
   Responsive - Tablet (≤920px)
   - Collapse to single column
   - Adjust padding and gaps
------------------------------------------------------------------ */
@media (max-width: 920px) {
  .filiali-page {
    padding: $space-1-5 $container-padding-x-mob;
    @include flex-gap($space-3, column);
  }
  
  .page-header { 
    padding: 0 $container-padding-x-mob; 
    margin-bottom: $space-1-5; 
  }
  
  .page-title { 
    font-size: $font-title-sm; 
  }
  
  .page-intro { 
    font-size: $font-base-sm; 
  }
  
  .groups-grid {
    grid-template-columns: 1fr;
    padding: 0 $container-padding-x-mob;
    gap: $space-1-5;
  }
}

/* ------------------------------------------------------------------
   Responsive - Mobile (≤480px)
   - Further reduce padding and font sizes
------------------------------------------------------------------ */
@media (max-width: 480px) {
  .filiali-page {
    padding: $space-1 $container-padding-x-mob;
    @include flex-gap($space-2, column);
  }
  
  .page-header {
    text-align: left;
  }

  .page-title { 
    font-size: $font-title-xs; 
    margin-left: 20px;   /* bordo a 24px: 24 + 20 = 44, come chi-siamo */
  }
  
  .page-intro { 
    font-size: $font-base-xs; 
    margin-left: 20px;
  }
}

/* ------------------------------------------------------------------
   Responsive - Extra Small Mobile (≤360px)
------------------------------------------------------------------ */
@media (max-width: 360px) {
  .page-title {
    font-size: $font-title-xxs;
    margin-left: 15px;   /* 24 + 15 = 39 */
  }

  .page-intro {
    margin-left: 15px;
  }
}
</style>